import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { useApp } from 'providers/useApp/AppContext';
import { useRecording } from 'providers/useRecording/RecordingContext';
import React, { FC, useEffect, useState } from 'react';
import { getHelmetData } from 'utils/fnHelmet';
import { secondsToTimeString } from 'utils/fnData';
import translate from 'utils/fnTranslate';
import { CenterButton, PageWrapper, Subtitle, ErrorMessage, Section } from 'components/Pages/Settings/PageSettingsCommon.css';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { secondsToTime } from 'utils/fnDate';
import { PageHeading } from 'components/PageHeading/PageHeading.css';
import { RecordingLabel, RecordingProgressIndicator, RecordingProgressLabels, Value, Name } from './PageRecordingsSettings.css';

export const PageRecordingsSettings: FC = () => {
    const { getRecordings, recordingsResponse, setToDeleteAll, fetchRecordingsError } = useRecording();
    const { setPageTitle } = useApp();

    const [remainingTime, setRemainingTime] = useState<number>(null);
    const [totalTime, setTotalTime] = useState<number>(null);
    const [progress, setProgress] = useState(0);
    const [buttonIsHidden, setButtonIsHidden] = useState<boolean>(false);

    useEffect(() => {
        setPageTitle(translate('SETTINGS_RECORDINGS'));
    }, []);

    useEffect(() => {
        setButtonIsHidden(true);
    }, [fetchRecordingsError]);

    useEffect(() => {
        getRecordings();
    }, []);

    useEffect(() => {
        if (recordingsResponse) {
            const { recording_limit, recording_limit_used } = recordingsResponse;

            setProgress((recording_limit_used / recording_limit) * 100);

            setButtonIsHidden(recording_limit_used === 0);

            setRemainingTime(recording_limit - recording_limit_used);
            setTotalTime(recording_limit);
        }
    }, [recordingsResponse]);

    const showAlert = () => {
        if (buttonIsHidden) {
            return;
        }
        setToDeleteAll(true);
    };

    const notAvailableValue = () => (fetchRecordingsError ? 'N/A' : undefined);

    return (
        <HelmetWrapper {...getHelmetData(translate('SETTINGS_RECORDINGS'))}>
            <PageWrapper>
                <PageHeading srOnly>{translate('SETTINGS_RECORDINGS')}</PageHeading>
                <Section aria-label={translate('SETTINGS_RECORDINGS_STORAGE')}>
                    <Subtitle data-test-id={'header-settingsbuttons-recording-remaining-time-title'}>
                        {translate('SETTINGS_RECORDINGS_STORAGE')}
                    </Subtitle>

                    <RecordingProgressIndicator>
                        <ProgressIndicator percentage={progress} displayWithNullProgress={true} />
                    </RecordingProgressIndicator>

                    <RecordingProgressLabels>
                        <RecordingLabel>
                            <Name>{translate('SETTINGS_RECORDINGS_STORAGE_TOTAL')}</Name>
                            <Value aria-label={secondsToTimeString(totalTime)}>{notAvailableValue() ?? secondsToTime(totalTime)}</Value>
                        </RecordingLabel>
                        <RecordingLabel>
                            <Name className={'highlighted'}>{translate('SETTINGS_RECORDINGS_STORAGE_RECORDED')}</Name>
                            <Value aria-label={secondsToTimeString(totalTime - remainingTime)}>
                                {notAvailableValue() ?? secondsToTime(totalTime - remainingTime)}
                            </Value>
                        </RecordingLabel>
                        <RecordingLabel>
                            <Name>{translate('SETTINGS_RECORDINGS_STORAGE_FREE')}</Name>
                            <Value aria-label={secondsToTimeString(remainingTime)}>
                                {notAvailableValue() ?? secondsToTime(remainingTime)}
                            </Value>
                        </RecordingLabel>
                    </RecordingProgressLabels>

                    <CenterButton
                        isDisabled={buttonIsHidden}
                        label={translate('DELETE_ALL_RECORDINGS_BUTTON')}
                        isPrimary={true}
                        onClick={showAlert}
                        data-test-id={'header-settingsbuttons-recordings-deleterecordings'}
                    />
                </Section>

                {fetchRecordingsError && <ErrorMessage>{translate('SETTINGS_ERROR_HINT')}</ErrorMessage>}
            </PageWrapper>
        </HelmetWrapper>
    );
};

export default PageRecordingsSettings;

export const DeleteAllRecordingsConfirmation: FC = () => {
    const { toDeleteAll, setToDeleteAll, deleteAllRecordings } = useRecording();

    if (!toDeleteAll) {
        return null;
    }

    return (
        <AlertDialog
            title={translate('ALERT_DELETE_ALL_RECORDINGS_TITLE')}
            bodyText={translate('ALERT_DELETE_ALL_RECORDINGS_BODY')}
            buttons={[
                {
                    text: translate('CANCEL_BUTTON'),
                    onClick: () => {
                        setToDeleteAll(false);
                    },
                },
                {
                    text: translate('DELETE_BUTTON'),
                    onClick: () => {
                        setToDeleteAll(false);
                        deleteAllRecordings();
                    },
                },
            ]}
        />
    );
};
